import { useRef, useState } from 'react';
import type { Contrato } from '../lib/api.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { usarFormulario } from '../lib/formulario.ts';
import { completarReais, digitarReais, mascararDinheiro, paraApi } from '../lib/dinheiro.ts';
import { NIVEIS } from '../lib/rotulos.ts';
import { EditorDeCobrancas, paraCobrancas, paraLinhas, type LinhaDeCobranca } from './EditorDeCobrancas.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { AjudaDoPasso, CampoDoPasso } from './PassoAPasso.tsx';
import { Valor } from './Valor.tsx';
import '../estilos/dinheiro-2026.css';

/**
 * Corrigir as cobranças de um contrato que já existe.
 *
 * A outra metade do "as duas coisas" que o Richard pediu em 15/08/2026: as
 * datas se combinam no cadastro **e** se corrigem depois — porque combinação
 * muda, e muda justamente depois de assinado.
 *
 * Aqui vai a lista inteira para a rota que a substitui, e o servidor confere a
 * soma e recusa mexer no que já foi baixado.
 *
 * ⚠️ **O total do contrato muda AQUI, e só aqui, depois do aceite.** Total e
 * cobranças são um ato só: a soma tem de fechar com o total, e alterá-los em
 * telas separadas deixaria o contrato inválido no meio do caminho.
 *
 * ⭐ **No desenho novo desde 18/09/2026** (a ficha em blocos, `docs/FORMULARIOS-
 * E-LINK-DO-CLIENTE.md`, A.4). E **gravar pergunta**, dizendo quantas cobranças
 * saem — a regra de todo formulário do Hub, que esta janela não seguia.
 */
export function CorrigirCobrancas({
  contrato,
  aoFechar,
  aoSalvar,
}: {
  contrato: Contrato;
  aoFechar: () => void;
  aoSalvar: (parcelas: ReturnType<typeof paraCobrancas>, valorTotal: string | undefined) => Promise<void>;
}) {
  const [linhas, setLinhas] = useState<LinhaDeCobranca[]>(() => paraLinhas(contrato.parcelas));
  const totalOriginal = (contrato.valorTotal ?? '0').replace('.', '');
  const [total, setTotal] = useState(() => mascararDinheiro(totalOriginal));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario({ linhas, total }, aoFechar);
  usarDialogo(guarda.fechar);

  const temPaga = contrato.parcelas.some((p) => p.pagoEm);
  const saem = contrato.parcelas.filter((p) => !linhas.some((l) => l.id === p.id)).length;

  function pedirParaSalvar() {
    if (emVoo.current) return;
    const novoTotal = paraApi(total);
    if (!novoTotal || Number(novoTotal) <= 0) return setErro('O total do contrato precisa ser maior que zero.');
    setErro(null);
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      const novoTotal = paraApi(total);
      // Só manda o total quando ele realmente mudou: escrita a menos é uma
      // chance a menos de gravar por engano.
      await aoSalvar(paraCobrancas(linhas), novoTotal === contrato.valorTotal ? undefined : novoTotal);
    } catch (e) {
      // A mensagem do servidor já diz quanto falta ou sobra, e qual parcela
      // está travada. Não vale reescrever aqui e arriscar dizer outra coisa.
      setErro(e instanceof Error ? e.message : 'Não consegui salvar as cobranças.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div className="imv cad-conferir din-ficha din-estreita" role="dialog" aria-modal="true" aria-label="Corrigir cobranças">
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">Ficha do cliente · corrigir cobranças</span>
              <h3 className="cad-titulo">
                Corrigir cobranças <span className="pap-titulo-codigo">{contrato.codigo}</span>
              </h3>
              <p className="cad-sub din-sub">
                {contrato.niveisDoContrato.map((x) => (
                  <span key={x.nivel} className={`selo-nivel selo-${x.nivel}`}>
                    {NIVEIS[x.nivel].longo}
                  </span>
                ))}
                <span>
                  total de <Valor de={contrato.valorTotal} />
                </span>
              </p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>

          <div className="imv-corpo">
            <div className="imv-conteudo">
              <section className="din-bloco">
                <div className="cad-subtitulo">
                  <h5>O total do contrato</h5>
                </div>
                <div className="imv-grade-campos">
                  <CampoDoPasso rotulo="Total" col={2} mono nota="É por aqui que o honorário muda depois do aceite.">
                    <i className="imv-pre">R$</i>
                    <input
                      id="cc-total"
                      inputMode="decimal"
                      value={total}
                      onChange={(e) => {
                        setErro(null);
                        setTotal(digitarReais(e.target.value));
                      }}
                      onBlur={(e) => setTotal(completarReais(e.target.value))}
                      placeholder="0,00"
                    />
                  </CampoDoPasso>
                </div>
              </section>

              <section className="din-bloco">
                <div className="cad-subtitulo">
                  <h5>As cobranças</h5>
                </div>
                <EditorDeCobrancas linhas={linhas} totalMascarado={total} aoMudar={setLinhas} />
              </section>
            </div>
            <AjudaDoPasso titulo="Antes de salvar">
              <p>
                A soma tem de <b>fechar com o total</b>; o servidor recusa o que não fecha, e diz quanto falta ou sobra.
              </p>
              {temPaga && <p>Cobrança já baixada fica travada — tire a baixa para mexer nela.</p>}
              <p>Data escrita aqui vale como está: o Hub não adia para dia útil o que você digitou.</p>
            </AjudaDoPasso>
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>
              Cancelar
            </button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={pedirParaSalvar}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : 'Salvar cobranças'}
            </button>
          </footer>
        </section>
      </div>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue={`as cobranças do contrato ${contrato.codigo}`}
        aviso={saem === 0 ? undefined : saem === 1 ? 'Uma cobrança sai do contrato, e não volta.' : `${saem} cobranças saem do contrato, e não voltam.`}
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </div>
  );
}
